/**
 * What needs an operator now (WP-16 rail badges, WP-18 the Dashboard's «Требует внимания»).
 * The rail and the Dashboard read the same requests under the same query keys, so a page view
 * fetches each once. Every item is gated by the role (a request the role may not make is never
 * sent) and is described in words (rule 21: a number or a colour alone is not a state).
 */
import type { BackupStatus } from '@/components/BackupsCard'
import { api } from '@/lib/api'
import { fmtInt } from '@/lib/format'
import { groupStats, type StatRow } from '@/lib/webhookLog'

/** a badge that failed to refresh keeps its last count without a toast (QueryErrorToasts) */
export const SILENT = { silent: true }
export const ATTENTION_REFRESH_MS = 60_000

export interface WebhookStats {
  payments: StatRow[] | null
  generation: StatRow[] | null
}

export const webhookStatsQuery = {
  queryKey: ['rail', 'webhooks'],
  queryFn: () => api.get<WebhookStats>('/webhooks/stats?hours=24'),
} as const

export const backupsQuery = {
  queryKey: ['rail', 'backups'],
  queryFn: () => api.get<BackupStatus>('/system/backups'),
} as const

/** Requests per payout status (GET /payouts/counts): the queue's tab counts. */
export const payoutCountsQuery = {
  queryKey: ['payouts', 'counts'],
  queryFn: () => api.get<{ counts: Record<string, number> }>('/payouts/counts'),
} as const

/** The rail badge of «Выплаты»: requests that wait for an operator's decision — new (pending)
 *  and «На проверке» (reviewing). */
export function railPayoutCount(counts: Record<string, number> | undefined): number {
  if (!counts) return 0
  return (counts.pending ?? 0) + (counts.reviewing ?? 0)
}

/** Failed generations among those created in the last 24 hours. */
export const failedJobsQuery = {
  queryKey: ['dashboard', 'attention'],
  queryFn: () => api.get<{ jobs_failed_24h: number }>('/dashboard/attention'),
} as const

export function plural(n: number, one: string, few: string, many: string): string {
  const d10 = n % 10
  const d100 = n % 100
  if (d10 === 1 && d100 !== 11) return one
  if (d10 >= 2 && d10 <= 4 && (d100 < 12 || d100 > 14)) return few
  return many
}

/** Webhook events not applied cleanly in 24 h, per log («payments» / «generation»). */
export function webhookProblems(s: WebhookStats | undefined): { payments: number; generation: number } {
  const sum = (rows: StatRow[] | null | undefined) => groupStats(rows).reduce((n, g) => n + g.problems, 0)
  return { payments: sum(s?.payments), generation: sum(s?.generation) }
}

export function backupFailed(b: BackupStatus | undefined): 'failed' | 'stale' | null {
  const st = b?.state
  if (st === 'failed' || st === 'verify_failed') return 'failed'
  if (st === 'stale') return 'stale'
  return null
}

export type AttentionTone = 'warn' | 'danger'

/** One item of «Требует внимания»: the phrase with its number, a badge word, where to go. */
export interface AttentionItem {
  id: 'jobs' | 'payouts' | 'webhooks' | 'backup'
  tone: AttentionTone
  /** the badge word: «Ошибки», «Ждут», «Сбой» */
  badge: string
  /** «Ошибок генерации за 24 ч: 312» */
  text: string
  /** the link's words: «Открыть заявки с ошибкой» */
  linkLabel: string
  to: string
}

export interface AttentionInput {
  jobsFailed?: number
  payoutCounts?: Record<string, number>
  webhooks?: WebhookStats
  backup?: BackupStatus
}

/** The items worth a look, in the order of the row (zeros are left out). */
export function attentionItems(a: AttentionInput): AttentionItem[] {
  const out: AttentionItem[] = []
  const failed = a.jobsFailed ?? 0
  if (failed > 0) {
    out.push({
      id: 'jobs',
      tone: 'danger',
      badge: 'Ошибки',
      text: `Ошибок генерации за 24 ч: ${fmtInt(failed)}`,
      linkLabel: 'Открыть заявки с ошибкой',
      to: '/jobs?status=failed',
    })
  }
  const pending = a.payoutCounts?.pending ?? 0
  const reviewing = a.payoutCounts?.reviewing ?? 0
  if (pending + reviewing > 0) {
    out.push({
      id: 'payouts',
      tone: 'warn',
      badge: 'Ждут',
      text: `Выплат ждут проверки: ${fmtInt(pending + reviewing)}`,
      linkLabel: 'Открыть очередь выплат',
      to: pending > 0 ? '/payouts' : '/payouts?status=reviewing',
    })
  }
  const hooks = webhookProblems(a.webhooks)
  if (hooks.payments + hooks.generation > 0) {
    out.push({
      id: 'webhooks',
      tone: 'warn',
      badge: 'Проверить',
      text: `Вебхуков требуют внимания: ${fmtInt(hooks.payments + hooks.generation)}`,
      linkLabel: 'Открыть журнал вебхуков',
      to: hooks.payments > 0 ? '/webhooks?tab=payments' : '/webhooks?tab=generation',
    })
  }
  const backup = backupFailed(a.backup)
  if (backup) {
    out.push({
      id: 'backup',
      tone: 'danger',
      badge: 'Сбой',
      text: backup === 'stale' ? 'Бэкап устарел' : 'Бэкап не прошёл проверку',
      linkLabel: 'Открыть «Систему»',
      to: '/system',
    })
  }
  return out
}
